/**
 * The task pins, drawn over the sheet (`MCK-20260521-008` — *"pin on the drawing reflects the
 * task's status"*, and the pin half of the audience criterion).
 *
 * **TWIN: `PinLayer.tsx`.** The projection mechanics are identical and deliberately duplicated
 * rather than abstracted: both write a marker's position onto its node inside the transform's
 * subscription, because re-running a component tree at 60 Hz to move a dozen dots is work
 * nobody asked for. What differs is everything a reader would want to see side by side — a
 * comment pin is a numbered teardrop that selects a card in the rail, a task pin is a round
 * marker coloured by status that opens the task. A shared component taking a `variant` would
 * hide that difference behind a prop. A change to the projection belongs in both files; this
 * sentence is the pointer (retro rule 12).
 *
 * **A marker on another page is not rendered**, exactly as in `PinLayer`. The task anchor
 * gained its `page` on 2026-09-25 (audit issue 163); before that this comparison was always true,
 * because `ViewerPage` had no page to put on the marker and built every one at 0.
 *
 * **Nothing here polls.** The list is the same cache entry the drawing's task list reads, and
 * every task mutation already sweeps it — so a status changed in this tab is on the sheet as
 * soon as the sweep lands. A status changed by somebody *else* arrives on the next refetch, and
 * the thing that would make it immediate is the push channel (`KLQ-20260814-004`), not a timer
 * this layer could start (CLAUDE.md, data fetching).
 */

import { useLayoutEffect, useMemo, useRef } from "react";

import { useMessages } from "@/lib/i18n";

import type { SheetAnchor, SheetTransform } from "./overlay";
import styles from "./Viewer.module.css";

/** One task, reduced to what a marker needs. */
export interface TaskPinMarker {
  readonly id: string;
  readonly anchor: SheetAnchor;
  readonly title: string;
  /** `done` and `closed` draw as settled; everything else is outstanding. */
  readonly settled: boolean;
}

export interface TaskPinLayerProps {
  /** `null` until the canvas has opened. No transform, no markers — never a guessed position. */
  readonly transform: SheetTransform | null;
  readonly markers: readonly TaskPinMarker[];
  readonly selectedTaskId: string | null;
  readonly onSelect: (taskId: string) => void;
}

export function TaskPinLayer({
  transform,
  markers,
  selectedTaskId,
  onSelect,
}: TaskPinLayerProps) {
  const t = useMessages("drawings");
  const nodes = useRef(new Map<string, HTMLElement>());

  // Memoised for `PinLayer`'s reason: a fresh array per render tears the subscription down and
  // rebuilds it on every keystroke anywhere above this layer.
  const placed = useMemo(() => markers, [markers]);

  useLayoutEffect(() => {
    if (transform === null) return undefined;
    const project = (): void => {
      for (const marker of placed) {
        const node = nodes.current.get(marker.id);
        if (node === undefined) continue;
        const at =
          marker.anchor.page === transform.page
            ? transform.toScreen(marker.anchor)
            : null;
        if (at === null) {
          node.style.visibility = "hidden";
          continue;
        }
        node.style.visibility = "visible";
        node.style.left = `${String(at.left)}px`;
        node.style.top = `${String(at.top)}px`;
      }
    };
    project();
    return transform.subscribe(project);
  }, [transform, placed]);

  if (transform === null) return null;

  return (
    <div className={styles.pinLayer}>
      {markers.map((marker) => (
        <button
          key={marker.id}
          type="button"
          ref={(node) => {
            if (node === null) nodes.current.delete(marker.id);
            else nodes.current.set(marker.id, node);
          }}
          className={[
            styles.taskPin,
            marker.settled ? styles.taskPinSettled : "",
            marker.id === selectedTaskId ? styles.taskPinSelected : "",
          ]
            .filter(Boolean)
            .join(" ")}
          // Positioned by the subscription above, so it starts off-screen rather than at the
          // sheet's corner: one frame of every pin stacked at 0,0 is a visible flicker.
          style={{ visibility: "hidden" }}
          aria-label={t("taskPin.label", { title: marker.title })}
          aria-pressed={marker.id === selectedTaskId}
          title={marker.title}
          onClick={() => {
            onSelect(marker.id);
          }}
        />
      ))}
    </div>
  );
}
